import React from 'react'
import AnonymousLayout from '@/layouts/Anonymous'

import { LoginPage } from '@/components/auth/loginPage'
import { ForgotPasswordPage } from '@/components/auth/forgotPassword'
import { ResetPasswordPage } from '@/components/auth/resetPassword'

import { KibanaFrame } from './pages/kibana/kibanaFrame'

import Home from './pages/Home'
import ResourcesAdministration from './components/administration/resources/Resources'
import DocumentationAdministration from './pages/administration/documentation'
import PasswordPolicy from './pages/administration/passwordPolicy/PasswordPolicy'
import UserProfile from '@/components/userProfile'
import CodeLists from './components/codeLists/codeListsEditor'
import CodeList from './components/codeLists/codeListEditor'
import { TasksList } from './pages/tasks/list'

import { OrganizationalUnit, OrganizationalUnitDetail } from './pages/organizationalUnits'

import TaskDetail from './pages/tasks/taskDetail'
import { IndividualDetail, IndividualList } from './pages/individuals'
import { CbsReportList } from './pages/individuals/cbsReports/list'
import { InstitutionGroups } from './pages/institutionGroups'
import { MyTicketDetail, MyTickets } from './pages/tickets'
import { DataCrossingComparison, DataCrossingDuplicates, DataCrossingProblematicCombinations } from './pages/dataCrossing'
import { ProblematicCombinationsDetail } from '@/internal-app/pages/dataCrossing/problematic-combinations-detail'
import { DataCrossingDuplicateDetail } from '@/internal-app/pages/dataCrossing/duplicate-detail'
import { DataCrossingSettings } from './pages/administration/dataCrossingSettings'

import { ReportingCycles, ReportingCyclesDetail } from './pages/reportingCycles'
import DocumentationViewer from '@/internal-app/pages/documentation'
import ReportingGroups from './pages/reportingGroups/reportingGroups'
import ReportingGroupDetail from './pages/reportingGroups/reportingGroupDetail'
import ReportingYearHolidays from './pages/years/ReportingYearHolidays'
import ReportingYears from './pages/years/ReportingYears'
import { ReportTemplateVersionDetail } from './pages/reporting/reportTemplateDetail'
import NotificationList from '@/components/notifications/list'
import { NotFound404 } from '@/components/404'
import { HelpPages } from '@/internal-app/pages/administration/helpPages'
import { TemporaryJobRunner } from '@/internal-app/pages/administration/temploraryJobRunner'
import OutputReportGroupDefinitions from './pages/outputReportGroupDefinitions'
import OutputReportGroupDefinitionDetail from './pages/outputReportGroupDefinitions/detail'
import OutputReportGenerator from './pages/outputReportGenerator'
import OutputReportDetail from './pages/outputReports/outputReportDetail'
import TestPage from './pages/TestPage'

import { MessageDetail, MessageReply, Messages } from '@/components/messages'

import { Detail as UserDetail, UsersManagementAddUser, UsersManagementList } from './pages/usersManagement'
import { BankDetail, BankList } from './pages/banks'
import { CompanyDetail, CompanyList } from './pages/companies'

import OutputReportGroups from './pages/outputReports/outputReportGroups'
import { OutputReportsPage } from './pages/outputReports/outputReports'

import InstitutionReportGroups from './pages/institutionReports/institutionReportGroups'
import InstitutionReports from './pages/institutionReports/institutionReports'
import InstitutionReportDetail from './pages/institutionReports/institutionReportDetail'

import { ExcelExport } from './pages/reporting/excelExport.js'
import ReportSubmissionGroups from './pages/reportSubmissionGroups/reportSubmissionGroups'
import ReportSubmissionsPage from './pages/reportSubmissionGroups/submissions/reportSubmissions'
import ReportSubmissionDetail from './pages/reportSubmissionGroups/submissions/detail/reportSubmissionDetail'
import AllReportSubmissions from './pages/reportSubmissionGroups/submissions/allReportSubmissions'
import ForecastReportSubmissionGroups from './pages/reportSubmissionGroups/forecastReportSubmissionGroups'
import ForecastAllReportSubmissions from './pages/reportSubmissionGroups/submissions/forecastAllReportSubmissions'
import ForecastReportSubmissionsPage from './pages/reportSubmissionGroups/submissions/forecastReportSubmissions'

import DashboardSettings from '@/components/dashboard/settings/page'
import Announcements from './pages/administration/announcements/announcements'
import AnnouncementDetail from './pages/administration/announcements/announcementDetail'
import ReportTemplatesList from './pages/administration/report-templates/list'
import ReportTemplateDetail from './pages/administration/report-templates/detail'
import BlackList from './pages/blackLists/list'
import BlackListExports from './pages/blacklistExports/list'
import { BlacklistDetail } from './pages/blackLists/detail'
import JobPositionPermissionDetail from './pages/jobPositionPermissions/detail'
import Fines from './pages/fines/list'
import { FineDetail } from './pages/fines/detail'
import { EntityNotFound } from '@/components/EntityNotFound'

import CreateReportSubmissionTemporary from './components/administration/test-report-group-submission-creator/create-report-submission'
import FilesTemporary from './pages/administration/TemporaryFileAttachments'
import { ReportDesignerPage } from '@/components/reportDesigner/page'
import BackgroundJobList from '@/components/backgroundJobs/list'
import SystemRsaKeysManagement from './pages/administration/systemRsaKeys'

import { QlikFrame } from '@/internal-app/pages/qlik/qlikFrame'
import { QlikDimensionsList } from '@/internal-app/pages/administration/qlik-dimensions'
import { QlikDimensionDetail } from '@/internal-app/pages/administration/qlik-dimensions/qlik-dimension-detail'

import SettingsPage from './pages/administration/settings'
import { CheckingRuleList } from './pages/administration/checking-rules'
import { WorkflowDefinitionDetail, WorkflowDefinitionList, WorkflowDefinitionVersionDetail } from './pages/administration/workflow-configuration'
import { InputReportsGenerator } from '@/internal-app/pages/inputReportsGenerator/inputReportsGenerator'
import { BlacklistReports } from '@/internal-app/pages/blacklistReports'
import { ReportConstantCategoryList } from '@/internal-app/pages/administration/report-constants'
import { ReportCategoryDetail } from '@/internal-app/pages/administration/report-constants/reportCategoryDetail'
import { IcbsReportAdministration } from '@/internal-app/pages/administration/icbsReportAdministration'

import { AuditLogs } from '@/internal-app/pages/auditLogs/list'
import { RtgsReports } from '@/internal-app/pages/rtgsReports/RtgsReports'
import { RtgsReportDetail } from '@/internal-app/pages/rtgsReports/RtgsReportDetail'

const scope = x => ({ path: `/:scope(${x})/:isChangeItemId(~)?:id(\\d+)`, exact: true })

const internalRoutes = [{
  exact: true,
  path: '/login',
  component: LoginPage,
  layout: AnonymousLayout
}, {
  exact: true,
  path: '/login/forgot',
  component: ForgotPasswordPage,
  layout: AnonymousLayout
}, {
  exact: true,
  path: '/login/reset',
  component: ResetPasswordPage,
  layout: AnonymousLayout
}, {
  path: '/kibana/:path(.*)',
  component: KibanaFrame,
  permission: ['CanViewSystemLogs', 'CanViewAuditLogs']
}, {
  path: '/audit-logs',
  component: AuditLogs,
  permission: ['CanViewSystemLogs', 'CanViewAuditLogs']
}, {
  exact: true,
  path: '/report-group-submission-temporary',
  component: CreateReportSubmissionTemporary,
  permission: 'CanViewTemporaryReportGroupSubmissions'
}, {
  exact: true,
  path: '/input-report-bulk-upload',
  component: InputReportsGenerator,
  permission: 'CanViewTemporaryReportGroupSubmissions'
}, {
  exact: true,
  path: '/resources-administration',
  component: ResourcesAdministration,
  permission: 'CanManageTranslations'
}, {
  exact: true,
  path: '/administration/documentation',
  component: DocumentationAdministration,
  permission: 'CanManageDocumentation'
}, {
  exact: true,
  path: '/documentation',
  component: DocumentationViewer
}, {
  exact: true,
  path: '/report-designer',
  component: ReportDesignerPage,
  permission: 'CanSeeReportDesigner'
}, {
  exact: true,
  path: '/individuals',
  component: IndividualList,
  permission: ['CanViewIndividuals', 'CanSeeCbiIndividuals']
}, {
  ...scope('individuals'),
  component: IndividualDetail,
  permission: ['CanViewIndividuals', 'CanSeeCbiIndividuals']
}, {
  exact: true,
  path: '/individuals/:id/cbs-reports',
  component: CbsReportList,
  permission: ['CanViewIndividuals', 'CanSeeCbiIndividuals']
}, {
  exact: true,
  path: '/banks',
  component: BankList,
  permission: 'CanViewBanks'
}, {
  ...scope('banks'),
  component: BankDetail,
  permission: 'CanViewBanks'
}, {
  exact: true,
  path: '/companies',
  component: CompanyList,
  permission: 'CanViewCompanies'
}, {
  ...scope('companies'),
  component: CompanyDetail,
  permission: 'CanViewCompanies'
}, {
  exact: true,
  path: '/password-policy',
  component: PasswordPolicy,
  permission: 'CanManagePasswordPolicy'
}, {
  exact: true,
  path: '/settings',
  component: SettingsPage,
  permission: 'CanManageSettings'
}, {
  exact: true,
  path: '/user-profile',
  component: UserProfile
}, {
  exact: true,
  path: '/dashboard-settings',
  component: DashboardSettings,
  componentProps: { enumName: 'InternalAppDashboardWidgetType' }
}, {
  exact: true,
  path: '/code-lists',
  component: CodeLists,
  permission: ['CanViewCodeListManagement', 'CanManageCodeLists']
}, {
  exact: true,
  path: '/code-lists/:type',
  component: CodeList,
  permission: ['CanViewCodeListManagement', 'CanManageCodeLists']
}, {
  exact: true,
  path: '/reporting-calendars',
  component: ReportingYears,
  permission: ['CanViewReportingCalendars', 'CanManageReportingCalendars']
}, {
  exact: true,
  path: '/reporting-calendars/:year(\\d+)/reporting-cycles',
  component: ReportingCycles,
  permission: ['CanViewReportingCalendars', 'CanManageReportingCalendars']
}, {
  exact: true,
  path: '/reporting-calendars/:year(\\d+)/reporting-cycles/:id(\\d+)',
  component: ReportingCyclesDetail,
  permission: ['CanViewReportingCalendars', 'CanManageReportingCalendars']
}, {
  exact: true,
  path: '/reporting-calendars/:year(\\d+)/holidays',
  component: ReportingYearHolidays,
  permission: ['CanViewReportingCalendars', 'CanManageReportingCalendars']
}, {
  exact: true,
  path: '/tasks',
  render: props => <TasksList {...props} />
}, {
  exact: true,
  path: '/tasks/:id',
  render: props => <TaskDetail {...props} />
}, {
  exact: true,
  path: '/all-tasks',
  render: props => <TasksList isAllTasks {...props} />
}, {
  exact: true,
  path: '/all-tasks/:id',
  render: props => <TaskDetail isAllTasks {...props} />
}, {
  exact: true,
  path: '/tickets/',
  component: MyTickets
}, {
  exact: true,
  path: '/tickets/:id',
  component: MyTicketDetail
}, {
  exact: true,
  path: '/organizational-units',
  component: OrganizationalUnit,
  permission: ['CanViewOrganizationalUnitManagement', 'CanManageOrganizationalUnits']
}, {
  exact: true,
  path: '/organizational-units/:id(\\d+)',
  render: props => <OrganizationalUnitDetail key={props.match.params.id} {...props} />,
  permission: ['CanViewOrganizationalUnitManagement', 'CanManageOrganizationalUnits']
}, {
  exact: true,
  path: '/organizational-units/:id(\\d+)/job-position-permissions/:jobAssignmentId(\\d+)',
  component: JobPositionPermissionDetail,
  permission: ['CanViewOrganizationalUnitManagement', 'CanManageOrganizationalUnits']
}, {
  path: '/test',
  component: TestPage,
  permission: 'CanUseTestingTools'
}, {
  exact: true,
  path: '/reporting-groups',
  component: ReportingGroups,
  permission: ['CanViewReportGroups', 'CanManageReportGroups']
}, {
  exact: true,
  path: '/reporting-groups/:id(\\d+)',
  component: ReportingGroupDetail,
  permission: ['CanViewReportGroups', 'CanManageReportGroups']
}, {
  exact: true,
  path: '/messages/:tab(\\w+)/new',
  render: props => <MessageDetail {...props} isNew backUrl={'/messages/' + props.match.params.tab} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanCreateDrafts']
}, {
  exact: true,
  path: '/messages/:id(\\d+)',
  render: props => <MessageDetail {...props} backUrl={'/messages/' + props.match.params.tab} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanCreateDrafts']
}, {
  exact: true,
  path: '/messages/draft/:id(\\d+)',
  render: props => <MessageDetail {...props} backUrl={'/messages/draft'} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanCreateDrafts']
}, {
  exact: true,
  path: '/messages/sent/:id(\\d+)',
  render: props => <MessageDetail {...props} backUrl={'/messages/sent'} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanCreateDrafts']
}, {
  exact: true,
  path: '/messages/:tab(\\w+)?',
  render: props => <Messages {...props} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanCreateDrafts']
}, {
  exact: true,
  path: '/messages/reply/:id(\\d+)/:replyId(\\d+)?',
  render: props => <MessageReply {...props} backUrl={'/messages/' + props.match.params.tab} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanCreateDrafts']
}, {
  exact: true,
  path: '/data-crossings-duplicates/:tabId(\\w+)?',
  component: DataCrossingDuplicates,
  permission: ['CanViewDataCrossingsDuplicates', 'CanManageDataCrossingsDuplicates']
}, {
  exact: true,
  path: '/data-crossings-duplicates/comparison-detail/:id(\\d+)',
  component: DataCrossingComparison,
  permission: ['CanViewDataCrossingsDuplicates', 'CanManageDataCrossingsDuplicates']
}, {
  exact: true,
  path: '/data-crossings-delinquents',
  component: DataCrossingProblematicCombinations,
  permission: ['CanViewDataCrossingsDelinquents', 'CanManageDataCrossingsDelinquents']
}, {
  exact: true,
  path: '/data-crossings-delinquents/:id(\\d+)',
  component: ProblematicCombinationsDetail,
  permission: ['CanViewDataCrossingsDelinquents', 'CanManageDataCrossingsDelinquents']
}, {
  exact: true,
  path: '/data-crossings-duplicates/duplicate-detail/:id(\\d+)',
  component: DataCrossingDuplicateDetail,
  permission: ['CanViewDataCrossingsDuplicates', 'CanManageDataCrossingsDuplicates']
}, {
  exact: true,
  path: '/administration/data-crossing',
  component: DataCrossingSettings,
  permission: 'CanManageDataCrossing'
}, {
  path: '/notifications',
  component: NotificationList
}, {
  path: '/institution-groups',
  component: InstitutionGroups,
  permission: ['CanViewInstitutionGroups', 'CanManageInstitutionGroups']
}, {
  exact: true,
  path: '/users',
  component: UsersManagementList,
  permission: 'CanManageUsers'
}, {
  exact: true,
  path: '/users/:id(\\d+)',
  component: UserDetail,
  permission: 'CanManageUsers'
}, {
  exact: true,
  path: '/users/add/:type(\\d+)',
  component: UsersManagementAddUser,
  permission: 'CanManageUsers'
}, {
  path: '/excel-export',
  component: ExcelExport,
  permission: 'CanViewExcelTest'
}, {
  exact: true,
  path: '/report-submission-groups',
  component: ReportSubmissionGroups,
  permission: 'CanViewReportSubmissions'
}, {
  exact: true,
  path: '/report-submission-groups/:parentId(\\d+)/report-submissions',
  component: ReportSubmissionsPage,
  permission: 'CanViewReportSubmissions'
}, {
  exact: true,
  path: '/report-submission-groups/:parentId(\\d+)/report-submissions/:id',
  component: ReportSubmissionDetail,
  permission: 'CanViewReportSubmissions'
}, {
  exact: true,
  path: '/all-report-submissions',
  component: AllReportSubmissions,
  permission: 'CanViewReportSubmissions'
}, {
  exact: true,
  path: '/forecast-report-submission-groups',
  component: ForecastReportSubmissionGroups,
  permission: 'CanViewForecastSubmissions'
}, {
  exact: true,
  path: '/forecast-report-submission-groups/:parentId(\\d+)/report-submissions',
  component: ForecastReportSubmissionsPage,
  permission: 'CanViewForecastSubmissions'
}, {
  exact: true,
  path: '/forecast-report-submission-groups/:parentId(\\d+)/report-submissions/:id',
  component: ReportSubmissionDetail,
  permission: 'CanViewForecastSubmissions'
}, {
  exact: true,
  path: '/forecast-all-report-submissions',
  component: ForecastAllReportSubmissions,
  permission: 'CanViewForecastSubmissions'
}, {
  exact: true,
  path: '/output-report-definitions',
  component: OutputReportGroupDefinitions,
  permission: 'CanManageOutputReportGroupDefinitions'
}, {
  exact: true,
  path: '/output-report-definitions/:id(\\d+)',
  component: OutputReportGroupDefinitionDetail,
  permission: 'CanManageOutputReportGroupDefinitions'
}, {
  exact: true,
  path: '/output-report-generator',
  component: OutputReportGenerator,
  permission: 'CanManageOutputReportGenerator'
}, {
  exact: true,
  path: '/blacklist-reports',
  component: BlacklistReports,
  permission: 'CanViewBlacklistReports'
}, {
  exact: true,
  path: '/output-report-groups',
  component: OutputReportGroups,
  permission: 'CanViewOutputReports'
}, {
  exact: true,
  path: '/rtgsreports',
  component: RtgsReports,
  permission: 'CanViewRtgsreports'
}, {
  ...scope('Rtgsreports'),
  component: RtgsReportDetail,
  permission: 'CanViewRtgsreports'
}, {
  exact: true,
  path: '/output-report-groups/:parentId(\\d+)/output-reports',
  component: OutputReportsPage,
  permission: 'CanViewOutputReports'
}, {
  exact: true,
  path: '/output-report-groups/:parentId(\\d+)/output-reports/:id',
  component: OutputReportDetail,
  permissions: 'CanViewOutputReports'
}, {
  exact: true,
  path: '/icbs-report-groups',
  component: InstitutionReportGroups,
  permission: ['CanViewICBSReportGroups', 'CanManageICBSReportGroups']
}, {
  exact: true,
  path: '/icbs-report-groups/:parentId(\\d+)/icbs-reports',
  component: InstitutionReports,
  permission: ['CanViewICBSReportGroups', 'CanManageICBSReportGroups']
}, {
  exact: true,
  path: '/icbs-report-groups/:parentId(\\d+)/icbs-reports/:id',
  component: InstitutionReportDetail,
  permissions: ['CanViewICBSReportGroups', 'CanManageICBSReportGroups']
}, {
  exact: true,
  path: '/qlik',
  component: QlikFrame,
  permission: 'CanAccessQlik'
}, {
  exact: true,
  path: '/icbs-report-administration',
  component: IcbsReportAdministration,
  permission: 'CanManageICBSReportGroups'
}, {
  exact: true,
  path: '/dimensions-qlik',
  component: QlikDimensionsList,
  permission: ['CanViewQlikDimensions', 'CanManageQlikDimensions']
}, {
  exact: true,
  path: '/dimensions-qlik-detail/:id(\\d+)',
  component: QlikDimensionDetail,
  permission: ['CanViewQlikDimensions', 'CanManageQlikDimensions']
}, {
  exact: true,
  path: '/announcements',
  component: Announcements,
  permission: 'CanManageAnnouncementSettings'
}, {
  exact: true,
  path: '/announcements/:id(\\d+)',
  component: AnnouncementDetail,
  permission: 'CanManageAnnouncementSettings'
}, {
  exact: true,
  path: '/administration-report-templates',
  component: ReportTemplatesList,
  permission: ['CanManageReportTemplates', 'CanViewReportTemplates']
}, {
  exact: true,
  path: '/administration-report-templates/:id(\\d+)',
  component: ReportTemplateDetail,
  permission: ['CanManageReportTemplates', 'CanViewReportTemplates']
}, {
  exact: true,
  path: '/administration-report-templates/:parentId(\\d+)/version/:id(\\d+)',
  component: ReportTemplateVersionDetail,
  permission: ['CanManageReportTemplates', 'CanViewReportTemplates']
}, {
  exact: true,
  path: '/system-rsa-keys',
  component: SystemRsaKeysManagement,
  permission: 'CanManageSystemRsaKeys'
}, {
  exact: true,
  path: '/workflow-configuration',
  component: WorkflowDefinitionList,
  permission: 'CanManageWorkflowConfiguration'
}, {
  exact: true,
  path: '/workflow-configuration/:id(\\d+)',
  component: WorkflowDefinitionDetail,
  permission: 'CanManageWorkflowConfiguration'
}, {
  exact: true,
  path: '/workflow-configuration/:parentId(\\d+)/version/:id(\\d+)',
  component: WorkflowDefinitionVersionDetail,
  permission: 'CanManageWorkflowConfiguration'
}, {
  exact: true,
  path: '/blacklists',
  component: BlackList,
  permission: 'CanViewBlackLists'
}, {
  exact: true,
  path: '/export-blacklists',
  component: BlackListExports,
  permission: 'CanViewBlacklistsExport'
}, {
  ...scope('blacklists'),
  component: BlacklistDetail,
  permission: ['CanViewBlackLists', 'CanViewBlacklistsExport']
}, {
  exact: true,
  path: '/fines',
  component: Fines,
  permission: 'CanViewFines'
}, {
  ...scope('fines'),
  component: FineDetail,
  permission: 'CanViewFines'
}, {
  exact: true,
  path: '/administration/help-pages',
  component: HelpPages,
  permission: 'CanManageHelp'
}, {
  exact: true,
  path: '/background-jobs',
  component: BackgroundJobList
}, {
  exact: true,
  path: '/checking-rules',
  component: CheckingRuleList,
  permission: ['CanViewCheckingRules', 'CanManageCheckingRules']
}, {
  exact: true,
  path: '/files-temporary',
  component: FilesTemporary,
  permission: 'CanViewTemporaryFileAttachments'
}, {
  exact: true,
  path: '/temporary-workflow-runner',
  component: TemporaryJobRunner,
  permission: ''
}, {
  exact: true,
  path: '/report-constant-categories',
  component: ReportConstantCategoryList,
  permission: ['CanViewReportingConstants', 'CanManageReportingConstants']
}, {
  exact: true,
  path: '/report-constant-categories/:id(\\d+)',
  component: ReportCategoryDetail,
  permission: ['CanViewReportingConstants', 'CanManageReportingConstants']
}, {
  exact: true,
  path: '/',
  component: Home
}, {
  exact: true,
  path: '/not-found',
  component: EntityNotFound
}, {
  component: NotFound404
}]

function getInternalRoutes () {
  return internalRoutes.map(x => { return { ...x } })
}

export { getInternalRoutes }



// WEBPACK FOOTER //
// ./src/internal-app/route-config.js